// frontend/src/admin/v2/components/LsmInventorySyncPanel.jsx
import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Box, Stack, Typography, Button, Checkbox, FormControlLabel,
  Alert, Skeleton, Chip,
} from '@mui/material'
import { SP, FS } from '../../../theme/tokens'
import Panel from '../../../components/common/Panel'
import { InlineConfirmButton } from '../../../components/common/confirm'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}
const JOB_KEY = 'lsm-inventory-sync'

// Poll cadence for the job-detail endpoint after triggering a run. 2s x 40
// tries = ~80s max wait before we give up and point the operator at the
// Recent runs list below (the run itself keeps going server-side either way).
const POLL_INTERVAL_MS = 2000
const POLL_MAX_TRIES = 40

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/** One label/value row, mirrors the Stats-card row style in JobDetail.jsx. */
function StatLine({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
      <span>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </Stack>
  )
}

/** Derives the operator-facing copy for a failed/aborted run from `summary.guards`. */
function guardAbortMessage(result) {
  const summary = result?.summary || {}
  const dz = summary.guards?.drop_to_zero
  if (dz && dz.status === 'abort') {
    const pct = Math.round((dz.ratio || 0) * 100)
    return `Refused: ${pct}% of stocked SKUs would drop to 0 — enable Force to override.`
  }
  const rc = summary.guards?.read_completeness
  if (rc && typeof rc === 'object' && rc.status === 'abort') {
    const distinct = rc.distinct_sku_count ?? summary.distinct_sku_count
    const floor = rc.min_expected_skus ?? summary.min_expected_skus
    return `Refused: LSM returned ${distinct} SKUs (below the ${floor} floor) — the read looks truncated. Nothing was written.`
  }
  return result?.lastError || 'The run failed — see Recent runs below for details.'
}

/** Structural placeholder for the result panel while a run is in flight. */
function ResultSkeleton() {
  return (
    <Stack spacing={SP.tight} sx={{ mt: SP.tight }}>
      <Skeleton variant="rounded" width={160} height={22} />
      <Skeleton variant="text" width="55%" />
      <Skeleton variant="text" width="45%" />
      <Skeleton variant="text" width="50%" />
      <Skeleton variant="rounded" height={90} />
    </Stack>
  )
}

/** Compact sku/current/target/committed table for `summary.diffs`. */
function DiffsTable({ diffs }) {
  if (!diffs || diffs.length === 0) {
    return (
      <Typography variant="body2" sx={{ fontSize: FS.xs, color: 'text.secondary', py: SP.tight }}>
        No inventory differences — Shopify already matches LSM.
      </Typography>
    )
  }
  return (
    <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
      <Stack direction="row" spacing={SP.tight} sx={{ fontSize: FS.xxs, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', pb: 0.25 }}>
        <Box sx={{ flex: '0 0 40%' }}>SKU</Box>
        <Box sx={{ flex: '0 0 30%' }}>Current → target</Box>
        <Box sx={{ flex: '0 0 30%' }}>Committed</Box>
      </Stack>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
        {diffs.map((d) => (
          <Stack key={d.sku} direction="row" spacing={SP.tight} sx={{ py: 0.375 }}>
            <Typography sx={{ flex: '0 0 40%', fontSize: FS.xs, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.sku}
            </Typography>
            <Typography sx={{ flex: '0 0 30%', fontSize: FS.xs, color: 'text.secondary' }}>
              {d.current} → {d.target}
            </Typography>
            <Typography sx={{ flex: '0 0 30%', fontSize: FS.xs, color: 'text.disabled' }}>
              {d.committed}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

/** Renders `result.summary` for a terminal (done) run. */
function ResultSummary({ result }) {
  const summary = result.summary || {}
  const diffs = summary.diffs || []
  const activatedCount = summary.activated?.length || 0
  // Task-3 quirk: needs_activation stays populated even after a successful
  // activate_missing run — relabel as the pre-run figure once we know SKUs
  // were actually activated this run, so the operator isn't told SKUs still
  // need activation that this run just handled.
  const needsActivationLabel = activatedCount > 0 ? 'Needed activation at run start' : 'Needs activation'

  return (
    <Stack spacing={SP.tight} sx={{ mt: SP.tight }}>
      <Stack direction="row" alignItems="center" spacing={SP.tight}>
        <Chip
          label={summary.dry_run ? 'Preview — nothing written' : 'Applied'}
          size="small"
          sx={{
            fontSize: FS.xs, fontWeight: 600,
            color: summary.dry_run ? 'info.main' : 'success.main',
            bgcolor: 'transparent',
            border: 1,
            borderColor: summary.dry_run ? 'info.main' : 'success.main',
          }}
        />
        {activatedCount > 0 && (
          <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            {`Activated ${activatedCount} this run`}
          </Typography>
        )}
      </Stack>

      <StatLine label="In scope" value={summary.in_scope ?? 0} />
      <StatLine label="Present at LSM location" value={summary.present ?? 0} />
      <StatLine label={needsActivationLabel} value={summary.needs_activation_count ?? 0} />
      <StatLine label="LSM SKUs read" value={`${summary.distinct_sku_count ?? '—'} (floor ${summary.min_expected_skus ?? '—'})`} />
      <StatLine label="Total committed (present SKUs)" value={summary.committed_summary?.total_committed ?? 0} />

      {!summary.dry_run && (
        <Stack direction="row" spacing={SP.md}>
          <StatLine label="Applied" value={summary.applied?.length ?? 0} />
          <StatLine label="Skipped" value={summary.skipped?.length ?? 0} />
        </Stack>
      )}

      <Typography variant="subtitle2" sx={{ fontSize: FS.sm, fontWeight: 600, mt: SP.tight }}>
        {`Diffs (${diffs.length})`}
      </Typography>
      <DiffsTable diffs={diffs} />
    </Stack>
  )
}

/**
 * LsmInventorySyncPanel — Sync-now dry-run/apply/force panel for the LSM
 * inventory sync job detail page.
 *
 * @description
 *   Feature-scoped panel rendered ONLY on /admin/jobs/lsm-inventory-sync
 *   (gated by key in JobDetail.jsx). The generic Run-now button on every
 *   other job card fires the cron endpoint bodyless -> dry-run only; this
 *   panel lets an operator request a live Apply (confirm-gated) and a
 *   Force override for the drop-to-zero guard, then polls the job detail
 *   endpoint and renders the run's `summary` (diffs, guard-abort reason,
 *   committed stock, activation count).
 *
 * @useWhen
 *   - Operator wants to preview or apply the daily LSM inventory sync on demand
 *   - Operator needs to override the drop-to-zero guard (Force)
 *   - Operator needs to activate SKUs not yet stocked at the LSM location
 *
 * @dontUseFor
 *   - Any other job's detail page — hardcoded to `lsm-inventory-sync`, not
 *     a generic job-run panel
 *
 * @related
 *   - JobCard (frontend/src/admin/v2/components/JobCard.jsx) — generic
 *     bodyless Run-now button (dry-run only); this panel adds apply/force
 *   - JobDetail (frontend/src/admin/v2/JobDetail.jsx) — hosts this panel
 *
 * @props
 *   onRan — optional callback fired after a run completes (parent refetch)
 */
export default function LsmInventorySyncPanel({ onRan }) {
  const [force, setForce] = useState(false)
  const [activateMissing, setActivateMissing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const cancelledRef = useRef(false)

  useEffect(() => () => { cancelledRef.current = true }, [])

  const runSync = useCallback(async (dryRun) => {
    const t0 = Date.now()
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      await axios.post(
        `${API}/api/admin/jobs/${JOB_KEY}/run`,
        { dry_run: dryRun, force, activate_missing: activateMissing },
        { headers: HEADERS },
      )
      let settled = false
      for (let tries = 0; tries < POLL_MAX_TRIES && !cancelledRef.current; tries += 1) {
        await sleep(POLL_INTERVAL_MS)
        if (cancelledRef.current) break
        const { data } = await axios.get(`${API}/api/admin/jobs/${JOB_KEY}`, { headers: HEADERS })
        const last = data?.last_run
        const isTerminal = last && (last.status === 'done' || last.status === 'error')
        const finishedAfterTrigger = last?.finished_at && Date.parse(last.finished_at) >= t0
        if (isTerminal && finishedAfterTrigger) {
          const { data: detail } = await axios.get(
            `${API}/api/admin/jobs/${JOB_KEY}/runs/${last.id}`,
            { headers: HEADERS },
          )
          if (!cancelledRef.current) {
            setResult({ status: detail.status, summary: detail.summary, lastError: detail.last_error })
            onRan?.()
          }
          settled = true
          break
        }
      }
      if (!settled && !cancelledRef.current) {
        setError('Timed out waiting for the run to finish — check Recent runs below.')
      }
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e.response?.data?.error || e.message || 'Failed to trigger the run.')
      }
    } finally {
      if (!cancelledRef.current) setBusy(false)
    }
  }, [force, activateMissing, onRan])

  const handleDryRun = () => runSync(true)

  return (
    <Panel pad="card">
      <Stack spacing={SP.card}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontSize: FS.sm, fontWeight: 600 }}>
            LSM inventory Sync-now
          </Typography>
          <Typography variant="body2" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            LSM is the source of truth for on_hand. Dry-run previews the diff; Apply writes it to Shopify.
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.card}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={force}
                disabled={busy}
                onChange={(e) => setForce(e.target.checked)}
              />
            )}
            label={(
              <Stack spacing={0}>
                <Typography sx={{ fontSize: FS.sm }}>Force</Typography>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                  {'override the drop-to-zero guard (30%+ of stocked SKUs -> 0)'}
                </Typography>
              </Stack>
            )}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={activateMissing}
                disabled={busy}
                onChange={(e) => setActivateMissing(e.target.checked)}
              />
            )}
            label={(
              <Stack spacing={0}>
                <Typography sx={{ fontSize: FS.sm }}>Activate missing</Typography>
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                  also activate the ~69 SKUs not yet stocked at the LSM location
                </Typography>
              </Stack>
            )}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.tight}>
          <Button size="small" variant="outlined" onClick={handleDryRun} disabled={busy}>
            Dry-run (preview)
          </Button>
          {/* Push nach Shopify -> Bestaetigung als InlineConfirmButton statt Dialog.
              🔴 Der entfernte Dialog sagte „This changes live inventory." — die
              Konsequenz eines Schreibzugriffs auf ein fremdes Lebendsystem darf
              nicht mit dem Dialog verschwinden. Sie steht als `promptDetail`
              unter der Rueckfrage und BRICHT UM, statt an der 220-px-Ellipse
              der Rueckfragezeile zu sterben (Gates zu PR 4, W8/W2). */}
          <InlineConfirmButton
            size="small"
            variant="contained"
            label="Apply (live)"
            prompt="Apply LSM inventory sync?"
            promptDetail="Writes LSM stock to Shopify on_hand for the LSM-fulfilled SKUs. This changes live inventory."
            onConfirm={() => runSync(false)}
            disabled={busy}
          />
        </Stack>

        {error && <Alert severity="error" variant="outlined">{error}</Alert>}

        {busy && <ResultSkeleton />}

        {!busy && result && (
          result.status === 'error'
            ? <Alert severity="error" variant="outlined">{guardAbortMessage(result)}</Alert>
            : <ResultSummary result={result} />
        )}
      </Stack>
    </Panel>
  )
}
